import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, CircularProgress, Alert, IconButton, Tooltip,
  Select, MenuItem, FormControl, InputLabel,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8082/api';

const FileImportTab = ({ colors }) => {
  const [channels, setChannels] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState('');
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('waide_access_token')}`,
    'Content-Type': 'application/json',
  });

  // Load FILE channels
  const loadChannels = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/v1/integration/channels`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error('Failed to load channels');
      const data = await res.json();
      const fileChannels = (data.channels || data || []).filter(
        (ch) => ch.channelType === 'FILE' && ch.isActive
      );
      setChannels(fileChannels);
      if (fileChannels.length > 0 && !selectedChannel) {
        setSelectedChannel(fileChannels[0].id);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedChannel]);

  useEffect(() => {
    loadChannels();
  }, []);

  // List files currently sitting in the channel's input directory.
  // Renders into the same result table as post-processing — files start with
  // status 'pending' and update to success/partial/error after processing.
  const loadFiles = useCallback(async (channelId) => {
    if (!channelId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/v1/integration/csv-import/channel/${channelId}/files`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-list files whenever the selected channel changes
  useEffect(() => {
    if (selectedChannel) loadFiles(selectedChannel);
  }, [selectedChannel, loadFiles]);

  // Process files for selected channel
  const handleProcessFiles = async () => {
    if (!selectedChannel) return;
    setProcessing(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/v1/integration/csv-import/channel/${selectedChannel}`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  // Refresh button: re-list files in the input directory (and channels)
  const handleRefresh = useCallback(() => {
    loadChannels();
    if (selectedChannel) loadFiles(selectedChannel);
  }, [loadChannels, loadFiles, selectedChannel]);

  const selectedChannelObj = channels.find((ch) => ch.id === selectedChannel);
  const inputDir = selectedChannelObj?.protocolConfig?.inputDir || 'N/A';

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ color: colors.textPrimary, mb: 2 }}>
        CSV File Import
      </Typography>
      <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 3 }}>
        Process CSV files from the input directory. Drop files matching the template format
        (items.csv, areas.csv, locations.csv, etc.) into the input folder, then click Process Files.
      </Typography>

      {/* Channel selector + Process button */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <FormControl size="small" sx={{ minWidth: 300 }}>
          <InputLabel sx={{ color: colors.textSecondary }}>File Import Channel</InputLabel>
          <Select
            value={selectedChannel}
            onChange={(e) => { setSelectedChannel(e.target.value); setResult(null); }}
            label="File Import Channel"
            sx={{ color: colors.textPrimary, '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border } }}
          >
            {channels.map((ch) => (
              <MenuItem key={ch.id} value={ch.id}>
                {ch.channelName} ({ch.channelCode})
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="contained"
          startIcon={processing ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
          onClick={handleProcessFiles}
          disabled={!selectedChannel || processing}
          sx={{ bgcolor: colors.success, '&:hover': { bgcolor: '#16a34a' }, textTransform: 'none' }}
        >
          {processing ? 'Processing...' : 'Process Files'}
        </Button>

        <Tooltip title="Refresh — re-list channels and files in the input directory">
          <IconButton onClick={handleRefresh} sx={{ color: colors.textMuted }}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Input directory info */}
      {selectedChannelObj && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, p: 1.5, bgcolor: colors.surfaceElevated, border: `1px solid ${colors.border}`, borderRadius: 1 }}>
          <FolderOpenIcon sx={{ color: colors.warning, fontSize: 18 }} />
          <Typography variant="body2" sx={{ color: colors.textSecondary }}>
            Input: <code style={{ color: colors.textPrimary }}>{inputDir}</code>
          </Typography>
        </Box>
      )}

      {/* Error */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Results */}
      {result && (
        <Box>
          {/* Summary */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            <Chip label={`${result.filesFound || 0} files in folder`} size="small" sx={{ bgcolor: colors.surfaceElevated, color: colors.textPrimary }} />
            {(result.processed > 0) && (
              <Chip label={`${result.processed} processed`} size="small" sx={{ bgcolor: 'rgba(34,197,94,0.15)', color: '#22c55e' }} />
            )}
            {result.errors > 0 && (
              <Chip label={`${result.errors} errors`} size="small" sx={{ bgcolor: 'rgba(239,68,68,0.15)', color: '#ef4444' }} />
            )}
            {result.files && result.files.some((f) => f.matched === false) && (
              <Chip label={`${result.files.filter((f) => f.matched === false).length} unmatched`} size="small" sx={{ bgcolor: 'rgba(245,158,11,0.15)', color: '#f59e0b' }} />
            )}
          </Box>

          {/* File results table */}
          {result.files && result.files.length > 0 && (
            <TableContainer sx={{ bgcolor: colors.surface, border: `1px solid ${colors.border}` }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: colors.textSecondary, fontWeight: 600, borderBottom: `1px solid ${colors.border}` }}>File</TableCell>
                    <TableCell sx={{ color: colors.textSecondary, fontWeight: 600, borderBottom: `1px solid ${colors.border}` }}>Entity</TableCell>
                    <TableCell sx={{ color: colors.textSecondary, fontWeight: 600, borderBottom: `1px solid ${colors.border}` }}>Rows</TableCell>
                    <TableCell sx={{ color: colors.textSecondary, fontWeight: 600, borderBottom: `1px solid ${colors.border}` }}>Created</TableCell>
                    <TableCell sx={{ color: colors.textSecondary, fontWeight: 600, borderBottom: `1px solid ${colors.border}` }}>Failed</TableCell>
                    <TableCell sx={{ color: colors.textSecondary, fontWeight: 600, borderBottom: `1px solid ${colors.border}` }}>Status</TableCell>
                    <TableCell sx={{ color: colors.textSecondary, fontWeight: 600, borderBottom: `1px solid ${colors.border}` }}>Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {result.files.map((file, idx) => (
                    <TableRow key={idx} sx={{ '&:hover': { bgcolor: colors.surfaceElevated } }}>
                      <TableCell sx={{ color: colors.textPrimary, borderBottom: `1px solid ${colors.border}`, fontFamily: 'monospace', fontSize: '0.85rem' }}>
                        {file.file}
                      </TableCell>
                      <TableCell sx={{ color: colors.textSecondary, borderBottom: `1px solid ${colors.border}` }}>
                        {file.targetEntity || '-'}
                      </TableCell>
                      <TableCell sx={{ color: colors.textPrimary, borderBottom: `1px solid ${colors.border}` }}>
                        {file.totalRows ?? '-'}
                      </TableCell>
                      <TableCell sx={{ color: '#22c55e', borderBottom: `1px solid ${colors.border}` }}>
                        {file.created ?? '-'}
                      </TableCell>
                      <TableCell sx={{ color: file.failed > 0 ? '#ef4444' : colors.textMuted, borderBottom: `1px solid ${colors.border}` }}>
                        {file.failed ?? '-'}
                      </TableCell>
                      <TableCell sx={{ borderBottom: `1px solid ${colors.border}` }}>
                        {file.status === 'success' && <Chip icon={<CheckCircleIcon />} label="Success" size="small" sx={{ bgcolor: 'rgba(34,197,94,0.15)', color: '#22c55e' }} />}
                        {file.status === 'partial' && <Chip icon={<WarningIcon />} label="Partial" size="small" sx={{ bgcolor: 'rgba(245,158,11,0.15)', color: '#f59e0b' }} />}
                        {file.status === 'error' && <Chip icon={<ErrorIcon />} label="Error" size="small" sx={{ bgcolor: 'rgba(239,68,68,0.15)', color: '#ef4444' }} />}
                        {file.status === 'pending' && file.matched !== false && (
                          <Chip label="Pending" size="small" sx={{ bgcolor: 'rgba(59,130,246,0.15)', color: '#3b82f6' }} />
                        )}
                        {file.matched === false && (
                          <Chip icon={<WarningIcon />} label="No pattern match" size="small" sx={{ bgcolor: 'rgba(156,163,175,0.15)', color: colors.textMuted }} />
                        )}
                      </TableCell>
                      <TableCell sx={{ color: colors.textMuted, borderBottom: `1px solid ${colors.border}`, fontSize: '0.8rem', maxWidth: 300 }}>
                        {file.error || (file.errors && file.errors.length > 0 ? file.errors.slice(0, 3).join('; ') : '')}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* No files message */}
          {result.filesFound === 0 && (
            <Box sx={{ p: 3, textAlign: 'center', bgcolor: colors.surfaceElevated, border: `1px solid ${colors.border}`, borderRadius: 1 }}>
              <FolderOpenIcon sx={{ fontSize: 40, color: colors.textMuted, mb: 1 }} />
              <Typography variant="body1" sx={{ color: colors.textSecondary }}>
                No CSV files found in the input directory.
              </Typography>
              <Typography variant="body2" sx={{ color: colors.textMuted, mt: 0.5 }}>
                Drop CSV files (items.csv, areas.csv, locations.csv, etc.) into: <code>{inputDir}</code>
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress size={24} />
        </Box>
      )}
    </Box>
  );
};

export default FileImportTab;
